import React, { useCallback, useEffect, useRef, useState } from 'react';
import QrReader from 'react-qr-scanner';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';
import BarcodeCamera from './BarcodeCamera';
import '../styles/devolucion.css';

const MISSING_REASONS = [
  { value: 'danado', label: 'Dañado' },
  { value: 'no_esta', label: 'No está en el carro' },
  { value: 'otro', label: 'Otro' },
];

const STATE_ORDER = { pendiente: 0, devuelto: 1, sin_pickear: 2 };

// Location QRs read "<rack>-<shelf>"; split the same way as the storage screens.
const parseLocation = (code) => {
  const [rack, shelf] = (code || '').trim().split('-');
  return rack && shelf ? { rack, shelf } : null;
};

const formatDate = (iso) => {
  if (!iso) return 'sin vencimiento';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const unitOf = (line) => (line.format_unit_kg ? 'kg' : line.format_name || 'un.');

const reasonLabel = (value) => MISSING_REASONS.find((r) => r.value === value)?.label || value;

// Same shape check as BarCode.js: "<sku>-XXXX".
const isLikelyBatchQr = (code, skuCode) => {
  const prefix = `${skuCode}-`;
  return code.startsWith(prefix) && /^[0-9A-Za-z]{4}$/.test(code.slice(prefix.length));
};

const lineDetail = (line) => {
  const unit = unitOf(line);
  if (line.state === 'sin_pickear') return 'Sin pickear';
  if (line.state === 'devuelto') {
    const r = line.return;
    const missing = r.missing_quantity > 0
      ? ` · Faltante: ${r.missing_quantity} (${reasonLabel(r.missing_reason)})`
      : '';
    return `Devuelto: ${r.returned_quantity} ${unit}${missing}`;
  }
  const s = line.suggested;
  if (!s) return `${line.picked_quantity} ${unit} · sin ubicación registrada`;
  const batch = s.batches[0];
  const batchText = batch ? ` · lote ${batch.batch} · vence ${formatDate(batch.expiration_date)}` : '';
  return `${line.picked_quantity} ${unit} → ${s.rack}-${s.shelf}${batchText}`;
};

function DevolucionCarro() {
  const { cart_code: cartCode } = useParams();
  const { token, userRole } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [freed, setFreed] = useState(false);
  const [line, setLine] = useState(null);
  const [step, setStep] = useState('location');
  const [location, setLocation] = useState(null);
  const [productCode, setProductCode] = useState('');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [typing, setTyping] = useState(false);
  // The camera keeps reporting the same QR; take one read per step.
  const scanLock = useRef(false);
  // The product camera reads continuously too: one code judged at a time,
  // and the last code found wrong is not judged again.
  const productLock = useRef(false);
  const rejectedCode = useRef('');
  const productInput = useRef(null);

  const homePath = userRole === 'admin' ? '/init' : '/picking';

  const request = useCallback((path, options = {}) => fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: `Token ${token}` },
  }), [token]);

  const loadCart = useCallback(async () => {
    try {
      const response = await request(`/cart-return/?cart_code=${encodeURIComponent(cartCode)}`);
      const body = await response.json();
      if (response.ok) {
        setCart(body);
        return body;
      }
      setLoadError(body.error || 'No se pudo cargar el carro.');
    } catch (err) {
      setLoadError('Error de conexión. Intenta de nuevo.');
    }
    return null;
  }, [request, cartCode]);

  useEffect(() => { loadCart(); }, [loadCart]);

  const openLine = (selected) => {
    setLine(selected);
    setStep('location');
    setLocation(null);
    setProductCode('');
    setQuantity(String(selected.picked_quantity));
    setReason('');
    setError('');
    setTyping(false);
    scanLock.current = false;
    productLock.current = false;
    rejectedCode.current = '';
  };

  const nothingInCart = () => {
    setLocation(null);
    setProductCode('');
    setQuantity('0');
    setReason('no_esta');
    setError('');
    setStep('quantity');
  };

  const acceptLocation = (code) => {
    if (code === line.sku_bar_code || isLikelyBatchQr(code, line.sku_code)) {
      setError('Escanea la ubicación, no el producto.');
      scanLock.current = false;
      return;
    }
    const parsed = parseLocation(code);
    if (!parsed) {
      setError(`Ubicación no válida: ${code}`);
      scanLock.current = false;
      return;
    }
    setError('');
    setLocation(parsed);
    const s = line.suggested;
    const elsewhere = s && (s.rack !== parsed.rack || s.shelf !== parsed.shelf);
    setStep(elsewhere ? 'confirmLocation' : 'product');
  };

  const handleScan = (result) => {
    if (!result || typeof result.text !== 'string' || scanLock.current) return;
    scanLock.current = true;
    acceptLocation(result.text);
  };

  const rescan = () => {
    scanLock.current = false;
    setLocation(null);
    setStep('location');
  };

  // Mirrors BarCode.js: the SKU barcode, a batch QR of this SKU, or (weighed
  // lines) a scale label containing the SKU code. The server checks again.
  const codeMatches = async (code) => {
    if (code === line.sku_bar_code) return true;
    if (line.format_unit_kg && code.includes(line.sku_code)) return true;
    if (!isLikelyBatchQr(code, line.sku_code)) return false;
    const response = await request('/decode-batch-qr/', { method: 'POST', body: JSON.stringify({ text: code }) });
    if (!response.ok) throw new Error('decode failed');
    const data = await response.json();
    return data.valid && data.sku === line.sku_code;
  };

  // Judges a product code, typed or scanned; a match moves on to the quantity.
  // Returns 'ok', 'wrong', or 'failed' when the code could not be judged.
  const checkProduct = async (code) => {
    setBusy(true);
    let verdict;
    try {
      verdict = (await codeMatches(code)) ? 'ok' : 'wrong';
    } catch (err) {
      verdict = 'failed';
    }
    setBusy(false);
    if (verdict === 'failed') {
      setError('No se pudo validar el código. Intenta de nuevo.');
    } else if (verdict === 'wrong') {
      setError(`El código ${code} no corresponde a ${line.sku_code}.`);
    } else {
      setError('');
      setProductCode(code);
      setStep('quantity');
    }
    return verdict;
  };

  const submitProduct = async (event) => {
    event.preventDefault();
    const code = productCode.trim();
    if (!code || busy) return;
    const verdict = await checkProduct(code);
    // A code that was never judged stays so the picker can retry it.
    if (verdict === 'wrong') setProductCode('');
    if (verdict !== 'ok' && productInput.current) productInput.current.focus();
  };

  const handleProductScan = async (text) => {
    const code = text.trim();
    if (!code || productLock.current || code === rejectedCode.current) return;
    productLock.current = true;
    const verdict = await checkProduct(code);
    if (verdict === 'wrong') rejectedCode.current = code;
    productLock.current = false;
  };

  const setTypingMode = (on) => {
    setTyping(on);
    setError('');
    rejectedCode.current = '';
  };

  const cameraFailed = () => {
    setTyping(true);
    setError('No se pudo abrir la cámara. Escribe el código.');
  };

  const picked = line ? Number(line.picked_quantity) : 0;
  const returned = Number(quantity);
  const isShort = quantity !== '' && returned < picked;

  const confirm = async () => {
    if (quantity === '' || Number.isNaN(returned) || returned < 0 || returned > picked) {
      setError(`La cantidad debe estar entre 0 y ${picked}.`);
      return;
    }
    if (isShort && !reason) {
      setError('Indica el motivo de lo que no se devuelve.');
      return;
    }
    setBusy(true);
    try {
      const response = await request('/cart-return/line/', {
        method: 'POST',
        body: JSON.stringify({
          cart_code: cartCode,
          item_id: line.item_id,
          rack: location ? location.rack : '',
          shelf: location ? location.shelf : '',
          scanned_code: productCode.trim(),
          returned_quantity: returned,
          missing_reason: isShort ? reason : '',
        }),
      });
      const body = await response.json();
      if (!response.ok) {
        setError(body.error || 'No se pudo registrar la devolución.');
        // The line may have been returned elsewhere meanwhile: refresh, and
        // leave the step if it no longer waits.
        const fresh = await loadCart();
        const current = fresh && fresh.lines.find((l) => l.item_id === line.item_id);
        if (current && current.state !== 'pendiente') setLine(null);
        return;
      }
      setError('');
      setLine(null);
      if (body.cart_freed) setFreed(true);
      else setCart(body);
    } catch (err) {
      setError('Error de conexión. Intenta de nuevo.');
    } finally {
      setBusy(false);
    }
  };

  const release = async () => {
    setBusy(true);
    try {
      const response = await request('/cart-return/release/', {
        method: 'POST',
        body: JSON.stringify({ cart_code: cartCode }),
      });
      const body = await response.json();
      if (response.ok) setFreed(true);
      else setError(body.error || 'No se pudo liberar el carro.');
    } catch (err) {
      setError('Error de conexión. Intenta de nuevo.');
    } finally {
      setBusy(false);
    }
  };

  const backButton = (
    <button
      type="button"
      className="devolucion-back"
      aria-label="Volver"
      onClick={() => {
        setError('');
        if (line) setLine(null);
        else navigate(homePath);
      }}
    >
      &#8592;
    </button>
  );

  if (freed) {
    return (
      <div className="devolucion-container">
        <div className="devolucion-freed">
          <h1>{cartCode} liberado ✓</h1>
          <p>Los productos quedaron registrados de vuelta en bodega.</p>
          <button type="button" className="devolucion-primary" onClick={() => navigate(homePath)}>
            Volver a Pedidos
          </button>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="devolucion-container">
        {backButton}
        <p className="devolucion-error" role="alert">{loadError}</p>
      </div>
    );
  }

  if (!cart) {
    return <div className="devolucion-container"><p>Cargando...</p></div>;
  }

  const renderStep = () => (
    <div className="devolucion-step">
      <h2>{line.sku_name}</h2>
      <p className="devolucion-line-detail">{lineDetail(line)}</p>

      {step === 'location' && (
        <>
          <p className="devolucion-instruction">
            {line.suggested
              ? `Escanea la ubicación ${line.suggested.rack}-${line.suggested.shelf}`
              : 'Escanea dónde lo dejarás'}
          </p>
          <div className="devolucion-camera">
            <QrReader
              delay={300}
              onScan={handleScan}
              onError={(err) => console.error('Error al escanear:', err)}
              style={{ width: '100%' }}
              constraints={{ video: { facingMode: 'environment' } }}
            />
          </div>
          <button type="button" className="devolucion-secondary" onClick={nothingInCart}>
            No hay nada de esta línea en el carro
          </button>
        </>
      )}

      {step === 'confirmLocation' && (
        <div className="devolucion-warning">
          <p>No es la ubicación original ({line.suggested.rack}-{line.suggested.shelf}).</p>
          <button type="button" className="devolucion-primary" onClick={() => setStep('product')}>
            Devolver aquí ({location.rack}-{location.shelf})
          </button>
          <button type="button" className="devolucion-secondary" onClick={rescan}>
            Escanear de nuevo
          </button>
        </div>
      )}

      {step === 'product' && !typing && (
        <>
          <p className="devolucion-instruction">Escanea el código de barras o el QR del producto</p>
          <div className="devolucion-camera">
            <BarcodeCamera onScan={handleProductScan} onError={cameraFailed} />
          </div>
          <button type="button" className="devolucion-secondary" onClick={() => setTypingMode(true)}>
            Escribir código
          </button>
        </>
      )}

      {step === 'product' && typing && (
        <form className="devolucion-form" onSubmit={submitProduct}>
          <label htmlFor="devolucion-product">Código del producto</label>
          <input
            id="devolucion-product"
            ref={productInput}
            autoFocus
            autoComplete="off"
            value={productCode}
            onChange={(e) => setProductCode(e.target.value)}
          />
          <button type="submit" className="devolucion-primary" disabled={busy}>Validar</button>
          <button type="button" className="devolucion-secondary" onClick={() => setTypingMode(false)}>
            Usar cámara
          </button>
        </form>
      )}

      {step === 'quantity' && (
        <div className="devolucion-form">
          <label htmlFor="devolucion-quantity">Cantidad devuelta ({unitOf(line)})</label>
          <input
            id="devolucion-quantity"
            type="number"
            inputMode="decimal"
            min="0"
            max={picked}
            step={line.format_unit_kg ? '0.001' : '1'}
            value={quantity}
            readOnly={!location}
            onChange={(e) => setQuantity(e.target.value)}
          />
          {location && <p className="devolucion-instruction">Va a {location.rack}-{location.shelf}</p>}
          {isShort && (
            <div className="devolucion-reasons" role="group" aria-label="Motivo">
              <p>¿Por qué no vuelve todo?</p>
              {MISSING_REASONS.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  className={`devolucion-chip${reason === r.value ? ' selected' : ''}`}
                  aria-pressed={reason === r.value}
                  onClick={() => setReason(r.value)}
                >
                  {r.label}
                </button>
              ))}
            </div>
          )}
          <button type="button" className="devolucion-primary" disabled={busy} onClick={confirm}>
            Confirmar
          </button>
        </div>
      )}
    </div>
  );

  const lines = [...cart.lines].sort((a, b) => STATE_ORDER[a.state] - STATE_ORDER[b.state]);
  const pendingCount = lines.filter((l) => l.state === 'pendiente').length;

  return (
    <div className="devolucion-container">
      {backButton}
      <h1 className="devolucion-title">Devolver {cart.cart_code}</h1>
      <p className="devolucion-subtitle">Pedido {cart.po_number} (anulado) · {cart.client}</p>
      {cart.cancel_reason && <p className="devolucion-reason">Motivo: {cart.cancel_reason}</p>}
      {error && <p className="devolucion-error" role="alert">{error}</p>}

      {line ? renderStep() : (
        <>
          <ul className="devolucion-lines">
            {lines.map((l) => (
              <li key={l.item_id}>
                {l.state === 'pendiente' ? (
                  <button type="button" className="devolucion-line" onClick={() => openLine(l)}>
                    <span className="devolucion-line-name">{l.sku_name}</span>
                    <span className="devolucion-line-detail">{lineDetail(l)}</span>
                  </button>
                ) : (
                  <div className="devolucion-line done">
                    <span className="devolucion-line-name">{l.sku_name}</span>
                    <span className="devolucion-line-detail">
                      {l.state === 'devuelto' ? '✓ ' : ''}{lineDetail(l)}
                    </span>
                  </div>
                )}
              </li>
            ))}
          </ul>
          {pendingCount === 0 && (
            <div className="devolucion-empty">
              <p>Nada que devolver.</p>
              <button type="button" className="devolucion-primary" disabled={busy} onClick={release}>
                Liberar carro
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default DevolucionCarro;
